/**
 * DiagnosticReporter — captura, sanitiza, guarda e envia incidentes da PWA.
 *
 * Vendor-neutral por desenho: o domínio fala com ESTE módulo, e ele fala com
 * `POST /api/v1/me/diagnostics`. Um provedor externo, se um dia entrar, entra
 * como outro `Transporte` aqui dentro — sem tocar em quem captura.
 *
 * Regras que não se negociam:
 *
 *   - **nunca quebra o app**: toda entrada é `try/catch`; falha do reporter é
 *     engolida (e não vira outro incidente — `emAndamento` corta recursão);
 *   - **sanitiza antes de guardar**: o que vai para a fila já passou por
 *     `@nv/core/diagnostics`; o servidor sanitiza de novo;
 *   - **fila local pequena, por conta, com TTL**: ≤ 20 itens, 7 dias, chave
 *     `nv:session:…` (varrida no logout pelo `clearScopedStorage`) e cada item
 *     carrega o `userId` de quem o gravou — outra conta nunca envia o que não
 *     é dela;
 *   - **sem sessão, sem envio**: o relatório fica só na memória da página;
 *     privacidade > completude;
 *   - **best-effort**: uma tentativa por gatilho (captura com rede, `online`,
 *     abertura), no máximo 3 por item, sem retry agressivo; 4xx descarta;
 *   - **estado transitório é da conta**: `ultimo`, o request id recente e o
 *     dedupe pertencem a quem estava logado. Toda entrada pública compara
 *     `usuarioAtual()` com o escopo anterior e, se mudou (A→null, null→B,
 *     A→B), zera tudo isso — B nunca vê, anexa ou herda nada de A. A fila
 *     persistida já tem `userId` por item; `serverReleaseVisto` fica porque é
 *     dado do servidor, não da pessoa;
 *   - **nenhuma mensagem livre**: `Error.message` de fonte não controlada não
 *     é guardado (regra estrutural em `@nv/core/diagnostics`); do stack só
 *     ficam os frames. Fontes controladas usam templates nossos.
 */
import {
  apenasFramesDoStack,
  classeDeErro,
  type ClientErrorReport,
  type ClientErrorSource,
  gerarDiagnosticCode,
  LIMITES,
  mensagemPersistivel,
  rotaGenerica,
  sanitizarTexto,
} from '@nv/core';

import { CLIENT_RELEASE, controladoPorServiceWorker, estaStandalone } from './release';

export interface Incidente {
  source: ClientErrorSource;
  error?: unknown;
  message?: string;
  componentStack?: string;
  requestId?: string;
  httpStatus?: number;
  /** Caminho em que aconteceu; vira rota genérica. Padrão: `location.pathname`. */
  route?: string;
}

export interface UltimoDiagnostico {
  /** Conta em cujo escopo o incidente aconteceu (`null` = sem sessão). */
  userId: string | null;
  diagnosticCode: string;
  occurredAt: string;
  route?: string;
  requestId?: string;
  source: ClientErrorSource;
  /** `true` quando o servidor já gravou (o código é o definitivo). */
  persistido: boolean;
}

export type Transporte = (relatorio: ClientErrorReport) => Promise<{ status: number; diagnosticCode?: string }>;

interface ItemDaFila {
  userId: string;
  relatorio: ClientErrorReport;
  criadoEm: number;
  tentativas: number;
}

export const CHAVE_FILA = 'nv:session:diagnosticos';
export const FILA_MAX = 20;
export const FILA_TTL_MS = 7 * 24 * 60 * 60_000;
export const TENTATIVAS_MAX = 3;
/** Anti-ruído: mais que isto por minuto vira só o primeiro. */
export const CAPTURAS_POR_MINUTO = 10;
export const JANELA_DEDUPE_MS = 60_000;
/** Por quanto tempo o Feedback oferece anexar o último incidente. */
export const JANELA_ULTIMO_MS = 30 * 60_000;

export interface ReporterDeps {
  usuarioAtual: () => string | null;
  transporte: Transporte;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  agora?: () => number;
  online?: () => boolean;
  rotaAtual?: () => string;
  /** Marca de erro já capturado (evita duplicar entre fetch e unhandledrejection). */
  marcar?: (erro: unknown) => void;
}

export class DiagnosticReporter {
  private ultimo: UltimoDiagnostico | null = null;
  private serverReleaseVisto: string | null = null;
  private ultimoRequestIdComFalha: { id: string; em: number } | null = null;
  private recentes: { assinatura: string; em: number }[] = [];
  private enviando = false;
  private emAndamento = false;
  /** Escopo (conta) a que o estado transitório pertence. */
  private escopo: string | null | undefined = undefined;

  constructor(private readonly deps: ReporterDeps) {}

  /**
   * Alinha o estado transitório à conta atual. Chamado em toda entrada
   * pública: se a conta mudou desde a última vez, o que era da anterior sai.
   */
  private sincronizarEscopo(): string | null {
    const atual = this.deps.usuarioAtual();
    if (this.escopo !== undefined && this.escopo !== atual) {
      this.ultimo = null;
      this.ultimoRequestIdComFalha = null;
      this.recentes = [];
    }
    this.escopo = atual;
    return atual;
  }

  /** Só pelo fetch instrumentado: o commit do servidor visto na última resposta. */
  registrarServerRelease(release: string | null): void {
    if (release && /^[A-Za-z0-9._-]{1,64}$/.test(release)) this.serverReleaseVisto = release;
  }

  registrarRequestIdComFalha(id: string | null): void {
    this.sincronizarEscopo();
    if (id) this.ultimoRequestIdComFalha = { id, em: this.agora() };
  }

  serverRelease(): string | null {
    return this.serverReleaseVisto;
  }

  /**
   * Captura um incidente. Devolve o código de diagnóstico (local até o
   * servidor confirmar) ou `null` se foi suprimido (ruído) ou se o próprio
   * reporter falhou. Nunca lança.
   */
  capturar(inc: Incidente): string | null {
    if (this.emAndamento) return null;
    this.emAndamento = true;
    try {
      const userId = this.sincronizarEscopo();
      const relatorio = this.montar(inc);
      if (!relatorio) return null;
      this.ultimo = {
        userId,
        diagnosticCode: relatorio.diagnosticCode,
        occurredAt: relatorio.occurredAt,
        source: relatorio.source,
        persistido: false,
        ...(relatorio.route ? { route: relatorio.route } : {}),
        ...(relatorio.requestId ? { requestId: relatorio.requestId } : {}),
      };
      if (!userId) return relatorio.diagnosticCode; // sem sessão: só na memória
      this.enfileirar({ userId, relatorio, criadoEm: this.agora(), tentativas: 0 });
      if (this.online()) void this.enviarPendentes();
      return relatorio.diagnosticCode;
    } catch {
      return null;
    } finally {
      this.emAndamento = false;
    }
  }

  /** O último incidente, se recente — o Feedback oferece anexá-lo. */
  ultimoRecente(): UltimoDiagnostico | null {
    const userId = this.sincronizarEscopo();
    if (!this.ultimo || this.ultimo.userId !== userId) return null;
    return this.agora() - Date.parse(this.ultimo.occurredAt) <= JANELA_ULTIMO_MS ? this.ultimo : null;
  }

  /** Itens da fila da conta atual (para testes e para o Feedback). */
  pendentes(): ClientErrorReport[] {
    const userId = this.sincronizarEscopo();
    return this.lerFila()
      .filter((i) => i.userId === userId)
      .map((i) => i.relatorio);
  }

  /** Envia o que houver da conta atual. Uma tentativa por item; nunca lança. */
  async enviarPendentes(): Promise<void> {
    if (this.enviando) return;
    this.enviando = true;
    try {
      const userId = this.sincronizarEscopo();
      if (!userId || !this.online()) return;
      const fila = this.lerFila();
      const restantes: ItemDaFila[] = [];
      for (const item of fila) {
        if (item.userId !== userId) {
          restantes.push(item); // não é nosso: fica (o logout da outra conta limpa)
          continue;
        }
        let resultado: { status: number; diagnosticCode?: string } | null = null;
        try {
          resultado = await this.deps.transporte(item.relatorio);
        } catch {
          resultado = null; // rede: fica para a próxima
        }
        if (resultado && resultado.status >= 200 && resultado.status < 300) {
          if (this.ultimo && this.ultimo.diagnosticCode === item.relatorio.diagnosticCode) {
            this.ultimo = { ...this.ultimo, persistido: true, diagnosticCode: resultado.diagnosticCode ?? this.ultimo.diagnosticCode };
          }
          continue;
        }
        if (resultado && resultado.status >= 400 && resultado.status < 500) continue; // recusado: não insiste
        const tentativas = item.tentativas + 1;
        if (tentativas < TENTATIVAS_MAX) restantes.push({ ...item, tentativas });
      }
      this.gravarFila(restantes);
    } catch {
      /* o reporter nunca derruba nada */
    } finally {
      this.enviando = false;
    }
  }

  /* ───────────────────────── internos ───────────────────────── */

  private montar(inc: Incidente): ClientErrorReport | null {
    const erro = inc.error;
    // A CLASSE do erro, de uma allowlist fixa — nunca `error.name` (gravável:
    // `e.name = 'Diabetes'`), nunca `code`, nunca o `name` de um objeto
    // rejeitado. Valor que não é Error vira categoria pelo tipo.
    const nome = erro === undefined ? undefined : classeDeErro(erro);
    // MENSAGEM: nunca o `Error.message`. Só o que NOSSO código monta para as
    // fontes controladas (templates); para as demais, nada — é a garantia
    // estrutural de que "Alergia severa a amendoim" numa exceção de tela não
    // chega a lugar nenhum (fila, POST, banco).
    const message = mensagemPersistivel(inc.source, inc.message);
    // STACK: só frames técnicos; a primeira linha (com a mensagem) cai fora.
    const stack = apenasFramesDoStack(erro instanceof Error ? erro.stack : undefined);

    const route = rotaGenerica(inc.route ?? this.deps.rotaAtual?.() ?? (typeof location !== 'undefined' ? location.pathname : '/'));

    // Anti-ruído: mesma assinatura no último minuto, ou mais de N por minuto.
    // A assinatura usa só dado estrutural (nunca a mensagem livre).
    const agora = this.agora();
    this.recentes = this.recentes.filter((r) => agora - r.em < JANELA_DEDUPE_MS);
    const primeiroFrame = stack?.split('\n')[0] ?? '';
    const assinatura = `${inc.source}|${route ?? ''}|${nome ?? ''}|${message ?? ''}|${inc.httpStatus ?? ''}|${primeiroFrame}`;
    if (this.recentes.some((r) => r.assinatura === assinatura)) return null;
    if (this.recentes.length >= CAPTURAS_POR_MINUTO) return null;
    this.recentes.push({ assinatura, em: agora });

    if (erro && this.deps.marcar) this.deps.marcar(erro);

    const requestId = inc.requestId ?? this.requestIdRecente();
    const relatorio: ClientErrorReport = {
      diagnosticCode: gerarDiagnosticCode(),
      occurredAt: new Date(agora).toISOString(),
      source: inc.source,
      clientRelease: CLIENT_RELEASE,
      online: this.online(),
      standalone: estaStandalone(),
      serviceWorkerControlled: controladoPorServiceWorker(),
    };
    const opc = <K extends keyof ClientErrorReport>(k: K, v: ClientErrorReport[K] | undefined) => {
      if (v !== undefined) relatorio[k] = v;
    };
    opc('errorName', nome);
    opc('message', message);
    opc('stack', stack);
    opc('componentStack', sanitizarTexto(inc.componentStack, LIMITES.componentStack));
    opc('route', route);
    opc('requestId', requestId && /^[A-Za-z0-9._-]{1,64}$/.test(requestId) ? requestId : undefined);
    opc('httpStatus', typeof inc.httpStatus === 'number' && Number.isInteger(inc.httpStatus) ? inc.httpStatus : undefined);
    opc('serverReleaseSeen', this.serverReleaseVisto ?? undefined);
    if (typeof navigator !== 'undefined') {
      opc('userAgent', sanitizarTexto(navigator.userAgent, LIMITES.userAgent));
      opc('language', sanitizarTexto(navigator.language, LIMITES.language));
    }
    if (typeof window !== 'undefined' && typeof window.innerWidth === 'number') {
      opc('viewportWidth', Math.max(0, Math.min(20000, Math.round(window.innerWidth))));
      opc('viewportHeight', Math.max(0, Math.min(20000, Math.round(window.innerHeight))));
    }
    const tripId = lerViagemAtiva(this.deps.storage ?? storagePadrao(), this.deps.usuarioAtual());
    opc('tripId', tripId);
    return relatorio;
  }

  private requestIdRecente(): string | undefined {
    if (!this.ultimoRequestIdComFalha) return undefined;
    return this.agora() - this.ultimoRequestIdComFalha.em <= JANELA_DEDUPE_MS ? this.ultimoRequestIdComFalha.id : undefined;
  }

  private enfileirar(item: ItemDaFila): void {
    const fila = this.lerFila();
    fila.push(item);
    while (fila.length > FILA_MAX) fila.shift();
    this.gravarFila(fila);
  }

  private lerFila(): ItemDaFila[] {
    const st = this.deps.storage ?? storagePadrao();
    if (!st) return [];
    try {
      const bruto = st.getItem(CHAVE_FILA);
      if (!bruto) return [];
      const lista = JSON.parse(bruto) as unknown;
      if (!Array.isArray(lista)) return [];
      const agora = this.agora();
      return lista.filter(
        (i): i is ItemDaFila =>
          !!i && typeof i === 'object' && typeof (i as ItemDaFila).userId === 'string' && typeof (i as ItemDaFila).criadoEm === 'number' && agora - (i as ItemDaFila).criadoEm <= FILA_TTL_MS && !!(i as ItemDaFila).relatorio,
      );
    } catch {
      return [];
    }
  }

  private gravarFila(fila: ItemDaFila[]): void {
    const st = this.deps.storage ?? storagePadrao();
    if (!st) return;
    try {
      if (fila.length === 0) st.removeItem(CHAVE_FILA);
      else st.setItem(CHAVE_FILA, JSON.stringify(fila));
    } catch {
      /* sem espaço: perde-se o diagnóstico, nunca a ação da pessoa */
    }
  }

  private agora(): number {
    return this.deps.agora?.() ?? Date.now();
  }

  private online(): boolean {
    return this.deps.online?.() ?? (typeof navigator === 'undefined' ? true : navigator.onLine);
  }
}

function storagePadrao(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}


/**
 * Viagem ativa da conta atual — a mesma chave escopada que `trip/viagemAtiva.ts`
 * grava (`nv:trip:ativa`, com o `userId` de quem escolheu). Só vale se for da
 * mesma conta; o servidor ainda confere a participação.
 */
function lerViagemAtiva(st: Pick<Storage, 'getItem'> | null, userId: string | null): string | undefined {
  if (!st || !userId) return undefined;
  try {
    const bruto = st.getItem('nv:trip:ativa');
    if (!bruto) return undefined;
    const v = JSON.parse(bruto) as { id?: unknown; userId?: unknown };
    return v.userId === userId && typeof v.id === 'string' ? v.id : undefined;
  } catch {
    return undefined;
  }
}

/** Transporte padrão: a rota first-party, marcada para o fetch instrumentado ignorar. */
export const transporteHttp: Transporte = async (relatorio) => {
  const res = await fetch('/api/v1/me/diagnostics', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', 'x-nv-diagnostico': '1' },
    body: JSON.stringify(relatorio),
  });
  let diagnosticCode: string | undefined;
  if (res.ok) {
    try {
      diagnosticCode = ((await res.json()) as { diagnosticCode?: string }).diagnosticCode;
    } catch {
      /* corpo inesperado: o código local continua valendo */
    }
  }
  return diagnosticCode ? { status: res.status, diagnosticCode } : { status: res.status };
};
